import type { PortalSettings } from '@l2u/shared/schemas';
import {
  clusterRegionOptions,
  contractorTypeOptions,
  stationOptions,
} from '@l2u/ui/lib/companyInfo.ts';
import { scopedOptions } from './adminAccess.ts';

/** Selectable values for the three company-info filter dimensions. */
export type CompanyFilterOptionPool = {
  contractorTypes: string[];
  clusterRegions: string[];
  stations: string[];
};

/** The slice of the signed-in admin the pool depends on. Per migration 0060 a
 *  NULL/empty assignment means unrestricted. */
export type FilterScopeUser = {
  assigned_contractor_types?: string[] | null;
  assigned_stations?: string[] | null;
};

/**
 * Option pool for the contractor type / cluster region / station filters,
 * shared by the vendor list, the Dashboard and Expiring Documents so the three
 * pages can never disagree about what is selectable.
 *
 * Every dimension comes from the Settings-curated list, in its configured
 * order — never from the values found on the loaded vendor rows. An admin
 * filters by the list they configured, and a value no vendor carries yet must
 * still be offered; selecting it simply returns no rows. Deriving cluster
 * region from the rows instead left its dropdown empty on a portal whose
 * vendors had no region set, while Contractor Type and Station showed their
 * full lists — the asymmetry this function exists to prevent. It is the same
 * rule the create-vendor modal already applies (Companies.tsx): a legitimately
 * configured region has to be usable before any vendor sits in it.
 *
 * Contractor type and station additionally narrow to the admin's assignment
 * scope. Cluster region has no assignment axis — there is no
 * `users.assigned_cluster_regions` column — so it skips `scopedOptions` and
 * every configured region stays selectable.
 *
 * Scope is orthogonal to tier (super-admin spec D14): a super admin's own
 * assigned_* narrows their filters exactly like an admin's, so there is no
 * role branch here to skip. The stored columns pass through raw because
 * `scopedOptions` already reads `[]` and `null` alike as unrestricted (D15) —
 * the same normalization `effectiveAssignment` applies for callers that need
 * the value itself, mirroring adminScope.ts on the server.
 */
export function companyFilterOptions(
  settings: Partial<PortalSettings> | undefined,
  user: FilterScopeUser | null | undefined,
): CompanyFilterOptionPool {
  return {
    contractorTypes: scopedOptions(
      contractorTypeOptions(settings),
      user?.assigned_contractor_types,
    ),
    clusterRegions: clusterRegionOptions(settings),
    stations: scopedOptions(stationOptions(settings), user?.assigned_stations),
  };
}
